import { Maximize2, Minus, Plus } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui';
import {
  computeBracketLayout,
  type BracketLayoutOptions,
} from '../model/bracketLayout';
import { roundAccent } from '../model/dashboardView';
import {
  matchAdvances,
  matchHasPair,
  type BracketMatch,
  type BracketRound,
} from '../model/bracketTypes';
import { BracketMatchCard } from './BracketMatchCard';

export interface BracketProps {
  rounds: readonly BracketRound[];
  showAvatars?: boolean;
  showFlags?: boolean;
  onMatchSelect?: (match: BracketMatch) => void;
  /** Nadpisanie wymiarow ukladu (szerokosc karty, odstepy). */
  layout?: Partial<BracketLayoutOptions>;
  /** Ukrywa przyciski powiekszenia. */
  hideZoomControls?: boolean;
  /**
   * Element za ostatnia runda — karta finalu/zwyciezcy z pucharem, jak
   * w makiecie. Pozycjonowany na srodku wysokosci finalu i laczony z nim linia.
   */
  finale?: ReactNode;
  /**
   * Zwezona kolumna finalu — dla karty bez nazwiska zwyciezcy (final jeszcze
   * nierozegrany). Pelna szerokosc karty meczu jest wtedy zmarnowana: w srodku
   * stoi sam puchar i slowo „Zwyciezca".
   */
  finaleCompact?: boolean;
  className?: string;
}

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.6;

/** Szerokosc kolumny finalu, gdy zwyciezca nie jest jeszcze znany. */
const COMPACT_FINALE_WIDTH = 128;
const ZOOM_STEP = 0.15;

/**
 * Drabinka turniejowa.
 *
 * Uklad liczy `computeBracketLayout`, wiec komponent radzi sobie z turniejem
 * dowolnej wielkosci — od 8 do 128 zawodnikow — bez zmian w kodzie.
 *
 * OSTROSC: linie laczace to sciezki SVG zlozone wylacznie z odcinkow poziomych
 * i pionowych o calkowitych wspolrzednych, rysowane z `shape-rendering:
 * crispEdges`. Wygladzanie krawedzi jest dla nich wylaczone, wiec przy kazdym
 * powiekszeniu pozostaja idealnie ostre. Powiekszenie realizuje transformacja
 * CSS na warstwie nadrzednej — tekst i grafika sa wektorowe, wiec skalowanie
 * nie wprowadza rozmycia.
 */
export function Bracket({
  rounds,
  showAvatars = false,
  showFlags = false,
  onMatchSelect,
  layout: layoutOverrides,
  hideZoomControls = false,
  finale,
  finaleCompact = false,
  className,
}: BracketProps) {
  const [zoom, setZoom] = useState(1);
  const viewportRef = useRef<HTMLDivElement>(null);

  // Debel/mixt układa nazwiska pary jedno pod drugim — karty muszą być wyższe.
  const hasPairs = useMemo(
    () => rounds.some((round) => round.matches.some(matchHasPair)),
    [rounds],
  );

  const layout = useMemo(
    () =>
      computeBracketLayout(rounds, {
        ...(hasPairs ? { matchHeight: 112 } : {}),
        ...(layoutOverrides ?? {}),
      }),
    [rounds, layoutOverrides, hasPairs],
  );

  const { matchWidth, headerHeight, matchHeight, columnGap } = layout.options;

  // Karta finalu siedzi kolumne dalej, wysrodkowana na osi ostatniego meczu.
  const finalMatch = layout.rounds[layout.rounds.length - 1]?.matches[0];
  const finaleCenterY = finalMatch ? finalMatch.y + matchHeight / 2 : 0;
  const finaleWidth = finaleCompact ? COMPACT_FINALE_WIDTH : matchWidth;
  const totalWidth = layout.width + (finale && finalMatch ? columnGap + finaleWidth : 0);
  // Zapas wysokosci, zeby wysrodkowana karta finalu nie wystawala poza obszar.
  const totalHeight =
    finale && finalMatch ? Math.max(layout.height, finaleCenterY + 130) : layout.height;

  const changeZoom = useCallback((delta: number) => {
    setZoom((current) => {
      const next = Math.round((current + delta) * 100) / 100;
      return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    });
  }, []);

  const fitToWidth = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || totalWidth === 0) return;

    // Zapas na wewnetrzne marginesy kontenera.
    const available = viewport.clientWidth - 24;
    const ratio = available / totalWidth;
    setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(ratio * 100) / 100)));
  }, [totalWidth]);

  if (rounds.length === 0) {
    return null;
  }

  return (
    <div className={cn('relative flex flex-col gap-3', className)}>
      {!hideZoomControls && (
        <div className="flex items-center justify-end gap-1.5">
          <IconButton
            icon={<Minus />}
            label="Pomniejsz drabinkę"
            size="sm"
            variant="secondary"
            disabled={zoom <= MIN_ZOOM}
            onClick={() => changeZoom(-ZOOM_STEP)}
          />
          <span className="text-fg-muted w-12 text-center text-xs tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <IconButton
            icon={<Plus />}
            label="Powiększ drabinkę"
            size="sm"
            variant="secondary"
            disabled={zoom >= MAX_ZOOM}
            onClick={() => changeZoom(ZOOM_STEP)}
          />
          <IconButton
            icon={<Maximize2 />}
            label="Dopasuj do szerokości"
            size="sm"
            variant="secondary"
            onClick={fitToWidth}
          />
        </div>
      )}

      <div ref={viewportRef} className="w-full overflow-auto">
        {/* Zewnetrzny element ma rozmiar PO powiekszeniu, zeby paski przewijania
            odpowiadaly temu, co faktycznie widac. */}
        <div
          style={{
            width: Math.ceil(totalWidth * zoom),
            height: Math.ceil(totalHeight * zoom),
          }}
        >
          <div
            className="relative"
            style={{
              width: totalWidth,
              height: totalHeight,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
            }}
          >
            <svg
              width={totalWidth}
              height={totalHeight}
              viewBox={`0 0 ${totalWidth} ${totalHeight}`}
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              shapeRendering="crispEdges"
            >
              {layout.connectors.map((connector) => (
                <path
                  key={connector.id}
                  d={connector.d}
                  fill="none"
                  strokeWidth={2}
                  stroke={
                    connector.highlighted ? 'var(--color-brand)' : 'var(--color-border-strong)'
                  }
                />
              ))}
              {/* Lacznik final -> karta finalu; w kolorze marki po rozstrzygnieciu. */}
              {finale && finalMatch && (
                <path
                  d={`M ${Math.round(finalMatch.x + matchWidth)} ${Math.round(finaleCenterY)} H ${Math.round(layout.width + columnGap)}`}
                  fill="none"
                  strokeWidth={2}
                  stroke={
                    matchAdvances(finalMatch.match)
                      ? 'var(--color-brand)'
                      : 'var(--color-border-strong)'
                  }
                />
              )}
            </svg>

            {layout.rounds.map((round) => (
              <div
                key={round.round.id}
                style={{ left: round.x, width: matchWidth, height: headerHeight }}
                className="absolute top-0 flex flex-col items-center justify-center"
              >
                {/* Akcent fazy — sam tekst, zeby naglowek nie konkurowal z zoltymi
                    kartami rozstrzygnietych meczow. */}
                <span
                  className={cn(
                    'text-[0.6875rem] font-bold tracking-[0.08em] uppercase',
                    roundAccent(round.round.id) ?? 'text-fg-muted',
                  )}
                >
                  {round.round.name}
                </span>
                {round.round.dateLabel && (
                  <span className="text-brand-text text-[0.6875rem] font-semibold">
                    {round.round.dateLabel}
                  </span>
                )}
              </div>
            ))}

            {layout.rounds.flatMap((round) =>
              round.matches.map((positioned) => (
                <div
                  key={positioned.match.id}
                  style={{
                    left: positioned.x,
                    top: positioned.y,
                    width: positioned.width,
                    height: positioned.height,
                  }}
                  className="absolute"
                >
                  <BracketMatchCard
                    match={positioned.match}
                    showAvatars={showAvatars}
                    showFlags={showFlags}
                    {...(onMatchSelect ? { onSelect: onMatchSelect } : {})}
                  />
                </div>
              )),
            )}

            {finale && finalMatch && (
              <div
                style={{
                  left: layout.width + columnGap,
                  top: finaleCenterY,
                  width: finaleWidth,
                  transform: 'translateY(-50%)',
                }}
                className="absolute"
              >
                {finale}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
